import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { adminGraphqlProxy } from "../helper/Common";
import { SHOPIFY_API_KEY, THEME_APP_EMBED_HANDLE, THEME_EDITOR_LINK_MODE } from "../config/env";
import { getThemesQuery } from "../graphql/themeQueries";
import { normalizeShopDomain, getShopFromWindow } from "../utils/shopDomain";

const EMBED_TARGETS = new Set(["body", "head", "compliance_head"]);

/**
 * Preferred: App Bridge `shopify.app.extensions()` — activation status on the published theme
 * (see Shopify App API docs). Avoids brittle JSON parsing when the client id in env matches
 * the storefront but URI formatting differs.
 */
async function embedActiveViaShopifyAppApi(embedHandle) {
  if (!embedHandle) return null;
  const shopify = typeof window !== "undefined" ? window.shopify : null;
  if (!shopify?.app?.extensions || typeof shopify.app.extensions !== "function") {
    return null;
  }
  try {
    const extensions = await shopify.app.extensions();
    if (!Array.isArray(extensions) || !extensions.length) return null;

    const themeExts = extensions.filter((e) => e.type === "theme_app_extension");
    if (!themeExts.length) return null;

    for (const ext of themeExts) {
      const acts = ext.activations || [];
      for (const a of acts) {
        if (!EMBED_TARGETS.has(a.target) || a.handle !== embedHandle) continue;
        return a.status === "active";
      }
    }
    return null;
  } catch (e) {
    console.warn("[useThemeEmbedStatus] shopify.app.extensions():", e);
    return null;
  }
}

/**
 * Detects whether this app's theme app embed is enabled (settings_data.json blocks).
 * Block type: shopify://apps/{apiKey}/blocks/{handle} (Shopify may add trailing slashes).
 */
function blockMatchesApp(blockType, apiKey, embedHandle) {
  if (!blockType || !embedHandle) return false;
  const t = String(blockType);
  const h = String(embedHandle).trim();
  const hasHandle =
    t.includes(`/blocks/${h}/`) ||
    t.includes(`/blocks/${h}`) ||
    t.endsWith(`/${h}`);
  if (!hasHandle) return false;
  if (!apiKey) return t.includes("shopify://apps/");
  const key = String(apiKey);
  if (t.includes(`shopify://apps/${key}/`)) return true;
  const m = t.match(/shopify:\/\/apps\/([^/]+)\//i);
  if (m && m[1].toLowerCase() === key.toLowerCase()) return true;
  return false;
}

function parseEmbedInactive(themesPayload, apiKey, embedHandle) {
  try {
    const edges = themesPayload?.data?.themes?.edges;
    if (!edges?.length) return { themeId: null, embedInactive: true };

    const themeNode = edges[0].node;
    const themeId = themeNode?.id || null;
    const files = themeNode?.files?.edges;

    if (!files?.length) {
      return { themeId, embedInactive: true };
    }

    const raw = files[0]?.node?.body?.content;
    if (!raw) return { themeId, embedInactive: true };

    const contentString = raw.replace(/\/\*[\s\S]*?\*\//g, "").trim();
    const settingsData = JSON.parse(contentString);
    const blocks = settingsData?.current?.blocks || settingsData?.blocks || {};

    const appBlocks = Object.values(blocks).filter((block) =>
      blockMatchesApp(block?.type, apiKey, embedHandle),
    );

    const hasEnabledBlock =
      appBlocks.length > 0 &&
      appBlocks.some(
        (block) => block.disabled === false || block.disabled === undefined,
      );

    return { themeId, embedInactive: !hasEnabledBlock };
  } catch (e) {
    console.error("[useThemeEmbedStatus] parse error:", e);
    return { themeId: null, embedInactive: true };
  }
}

function shopNameFromDomain(shop) {
  if (!shop) return "";
  let s = String(shop).trim();
  if (s.includes(".myshopify.com")) {
    s = s.replace(/\.myshopify\.com.*$/i, "");
  }
  const storePrefix = s.match(/^store\/([^/]+)/i);
  if (storePrefix) {
    return storePrefix[1];
  }
  return s.split("/")[0] || "";
}

/** GraphQL `errors` may be an array, a string, or a single object depending on the client/proxy. */
function normalizeGraphqlErrors(res) {
  const raw = res?.errors;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") return [{ message: raw }];
  if (typeof raw === "object" && raw.message) return [raw];
  return [{ message: String(raw) }];
}

function errorMessagesList(errors) {
  return errors.map((e) =>
    typeof e === "string" ? e : e?.message || JSON.stringify(e),
  );
}

/** One in-flight GetStoreThemes per shop (React Strict Mode / duplicate mounts). */
const themesFetchByShop = new Map();

async function fetchStoreThemesGraphql(shopKey, forceRefresh) {
  const key = shopKey || "__default__";
  const existing = themesFetchByShop.get(key);
  if (existing && !forceRefresh) {
    return existing;
  }
  const promise = adminGraphqlProxy(getThemesQuery(forceRefresh)).finally(() => {
    if (themesFetchByShop.get(key) === promise) {
      themesFetchByShop.delete(key);
    }
  });
  themesFetchByShop.set(key, promise);
  return promise;
}

export function useThemeEmbedStatus(shopFromRedux) {
  const shop = useMemo(() => {
    const fromRedux = normalizeShopDomain(shopFromRedux);
    if (fromRedux) return fromRedux;
    return getShopFromWindow();
  }, [shopFromRedux]);

  const [themeLoading, setThemeLoading] = useState(true);
  /** True while re-fetching after the first load (refresh) — keeps UI mounted; use for button spinners only. */
  const [themeRefreshing, setThemeRefreshing] = useState(false);
  const [themeId, setThemeId] = useState(null);
  const [embedInactive, setEmbedInactive] = useState(true);
  const [themeCheckError, setThemeCheckError] = useState(null);
  const initialThemeFetchDoneRef = useRef(false);

  const fetchStatus = useCallback(async (options = {}) => {
    const force = options.force === true;
    const effectiveShop = shop || getShopFromWindow();
    if (!effectiveShop) {
      setThemeLoading(true);
      setThemeRefreshing(false);
      return;
    }
    const apiKey = SHOPIFY_API_KEY;
    if (!apiKey) {
      setThemeLoading(false);
      setThemeRefreshing(false);
      setThemeCheckError("REACT_APP_SHOPIFY_API_KEY is not set.");
      return;
    }

    const isInitialFetch = !initialThemeFetchDoneRef.current;
    if (isInitialFetch) {
      setThemeLoading(true);
    } else {
      setThemeRefreshing(true);
    }
    setThemeCheckError(null);
    try {
      const res = await fetchStoreThemesGraphql(effectiveShop, force);
      const gqlErrors = normalizeGraphqlErrors(res);

      const handle = THEME_APP_EMBED_HANDLE;
      const viaAppApi = await embedActiveViaShopifyAppApi(handle);

      if (gqlErrors.length && viaAppApi !== true && viaAppApi !== false) {
        console.warn("[useThemeEmbedStatus] GraphQL errors:", gqlErrors);
        const msg = errorMessagesList(gqlErrors).join("; ") || "Theme query failed";
        setThemeCheckError(msg);
        setEmbedInactive(true);
        setThemeId(null);
        return;
      }

      const parsed = parseEmbedInactive(res, apiKey, handle);

      let inactive;
      if (viaAppApi === true) {
        inactive = false;
      } else if (viaAppApi === false) {
        inactive = true;
      } else {
        inactive = parsed.embedInactive;
      }

      setThemeId(parsed.themeId);
      setEmbedInactive(inactive);
      setThemeCheckError(null);
    } catch (e) {
      console.error("[useThemeEmbedStatus]", e);
      setThemeCheckError(e?.message || "Theme check failed");
      setEmbedInactive(true);
      setThemeId(null);
    } finally {
      setThemeLoading(false);
      setThemeRefreshing(false);
      initialThemeFetchDoneRef.current = true;
    }
  }, [shop]);

  const refresh = useCallback(() => fetchStatus({ force: true }), [fetchStatus]);

  const lastFetchedShopRef = useRef(null);

  useEffect(() => {
    const effectiveShop = shop || getShopFromWindow();
    if (!effectiveShop) {
      setThemeLoading(true);
      return;
    }
    if (lastFetchedShopRef.current === effectiveShop && initialThemeFetchDoneRef.current) {
      return;
    }
    lastFetchedShopRef.current = effectiveShop;
    fetchStatus({ force: false });
  }, [shop, fetchStatus]);

  /**
   * Deep link to theme editor + app embed panel.
   * Always use `{shop}.myshopify.com/admin/themes/...` (same as Admin_react). Using
   * `admin.shopify.com/store/{handle}/themes/...` inside the embedded admin often produces
   * `/store/handle/store/handle/themes/...` when App Bridge applies store context again.
   */
  const getThemeEditorDeepLink = useCallback(() => {
    if (!shop || !SHOPIFY_API_KEY) return null;
    const storeHandle = shopNameFromDomain(shop);
    if (!storeHandle) return null;

    const handle = THEME_APP_EMBED_HANDLE || "calendar";
    const adminBase = `https://${storeHandle}.myshopify.com/admin`;

    if (THEME_EDITOR_LINK_MODE === "embeds_only") {
      return `${adminBase}/themes/current/editor?context=apps&template=index`;
    }

    const appEmbedParam = encodeURIComponent(`${SHOPIFY_API_KEY}/${handle}`);

    return `${adminBase}/themes/current/editor?context=apps&template=index&activateAppId=${appEmbedParam}`;
  }, [shop]);

  return {
    themeLoading,
    themeRefreshing,
    themeId,
    embedInactive,
    themeCheckError,
    refresh,
    fetchStatus,
    getThemeEditorDeepLink,
  };
}
